import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  ScriptableContext
} from 'chart.js';
import { NetWorthByYear } from './rentVsBuyEngine';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

// Same colours as the Net Worth Race bars below the chart
const BUYER_COLOR = '#3b82f6';
const RENTER_COLOR = '#f59e0b';
const GRID_COLOR = '#e5e7eb';

interface NetWorthRaceChartProps {
  series: NetWorthByYear[];
  leadFromYear: number;
  winner: 'buy' | 'rent' | 'tie';
  formatCurrency: (value: number) => string;
}

/** Axis labels like $300k so they fit; the tooltip shows the exact figure. */
const compactCurrency = (value: number): string => {
  const sign = value < 0 ? '-' : '';
  const size = Math.abs(value);
  return size >= 1000 ? `${sign}$${Math.round(size / 1000)}k` : `${sign}$${Math.round(size)}`;
};

/** Buyer vs renter wealth year by year. The point where the winner takes the lead for good is enlarged. */
const NetWorthRaceChart: React.FC<NetWorthRaceChartProps> = ({ series, leadFromYear, winner, formatCurrency }) => {
  const leadIndex = leadFromYear - 1;
  const pointRadiusFor = (side: 'buy' | 'rent') => (context: ScriptableContext<'line'>) =>
    winner === side && leadFromYear > 1 && context.dataIndex === leadIndex ? 7 : 3;

  const data: ChartData<'line', number[], string> = {
    labels: series.map(point => `Year ${point.year}`),
    datasets: [
      {
        label: 'Buyer net worth',
        data: series.map(point => point.ownerNetWorth),
        borderColor: BUYER_COLOR,
        backgroundColor: BUYER_COLOR,
        borderWidth: 2,
        pointRadius: pointRadiusFor('buy'),
        pointHoverRadius: 6,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        tension: 0.2
      },
      {
        label: 'Renter net worth',
        data: series.map(point => point.renterNetWorth),
        borderColor: RENTER_COLOR,
        backgroundColor: RENTER_COLOR,
        borderWidth: 2,
        pointRadius: pointRadiusFor('rent'),
        pointHoverRadius: 6,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        tension: 0.2
      }
    ]
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top', align: 'start', labels: { boxWidth: 12, boxHeight: 12 } },
      tooltip: {
        callbacks: {
          label: context => `${context.dataset.label}: ${formatCurrency(context.parsed.y ?? 0)}`
        }
      }
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        grid: { color: GRID_COLOR },
        ticks: { callback: value => compactCurrency(Number(value)) }
      }
    }
  };

  return (
    <div style={{ height: '280px', marginBottom: '1.5rem' }}>
      <Line data={data} options={options} />
    </div>
  );
};

export default NetWorthRaceChart;
